import { relationshipConfig, interestOptions, occasionOptions, genderOptions } from './relationshipConfig';

import { Label } from '@/components/ui/label';
import { Plus, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
export default function AddRecipientDialog({
  isOpen,
  onClose,
  newRecipient,
  onRecipientChange,
  onSubmit,
  onInterestToggle,
  onAddOccasion,
  onRemoveOccasion,
  onOccasionDateChange,
  darkMode
}) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" style={{ backgroundColor: darkMode.isDark ? '#2D4354' : 'white' }}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Playfair Display, serif', fontSize: '28px', color: darkMode.isDark ? '#FED7A5' : '#20212B' }}>
            Create Keepsake
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-5 mt-4">
          <div>
            <Label className="text-sm font-medium" style={{ color: darkMode.isDark ? '#FED7A5' : '#20212B' }}>Who is this for?</Label>
            <Input
              value={newRecipient.name}
              onChange={(e) => onRecipientChange({ ...newRecipient, name: e.target.value })}
              data-testid="recipient-name-input"
              required
              placeholder="e.g., Mom, Best Friend Sarah, Partner"
              className="mt-2"
              style={{ 
                backgroundColor: darkMode.isDark ? 'rgba(255, 255, 255, 0.1)' : 'white',
                color: darkMode.isDark ? '#FED7A5' : '#1F2937',
                borderColor: darkMode.isDark ? '#FED7A5' : '#E5E5E5'
              }}
            />
          </div>

          <div>
            <Label className="text-sm font-medium" style={{ color: darkMode.isDark ? '#FED7A5' : '#20212B' }}>Relationship</Label>
            <Select
              value={newRecipient.relationship}
              onValueChange={(val) => onRecipientChange({ ...newRecipient, relationship: val })}
            >
              <SelectTrigger data-testid="recipient-relationship-select" className="mt-2" style={{ 
                backgroundColor: darkMode.isDark ? 'rgba(255, 255, 255, 0.1)' : 'white',
                color: darkMode.isDark ? '#FED7A5' : '#1F2937',
                borderColor: darkMode.isDark ? '#FED7A5' : '#E5E5E5'
              }}>
                <SelectValue placeholder="Select relationship" />
              </SelectTrigger>
              <SelectContent>
                {Object.keys(relationshipConfig).map(rel => (
                  <SelectItem key={rel} value={rel}>{rel}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm font-medium mb-2 block" style={{ color: darkMode.isDark ? '#FED7A5' : '#20212B' }}>Shopping for</Label>
            <div className="flex gap-2">
              {genderOptions.map(g => (
                <button
                  key={g.label}
                  type="button"
                  data-testid={`gender-${g.label.toLowerCase()}`}
                  onClick={() => onRecipientChange({ ...newRecipient, gender: g.value })}
                  className="px-4 py-2 rounded-full text-sm font-medium transition-all"
                  style={{
                    background: (newRecipient.gender || '') === g.value ? '#9E6752' : (darkMode.isDark ? 'rgba(255, 255, 255, 0.1)' : '#F5F5F5'),
                    color: (newRecipient.gender || '') === g.value ? 'white' : (darkMode.isDark ? '#FED7A5' : '#534145'),
                    border: `1px solid ${(newRecipient.gender || '') === g.value ? '#9E6752' : (darkMode.isDark ? '#FED7A5' : '#E5E5E5')}`
                  }}
                >
                  {g.label}
                </button>
              ))}
            </div>
            <p className="text-xs mt-1.5" style={{ color: darkMode.isDark ? 'rgba(254, 215, 165, 0.7)' : '#73766A' }}>
              Keeps recommendations styled right, e.g. no feminine picks for him
            </p>
          </div>

          <div>
            <Label className="text-sm font-medium mb-3 block" style={{ color: darkMode.isDark ? '#FED7A5' : '#20212B' }}>Their Interests</Label>
            <div className="flex flex-wrap gap-2">
              {interestOptions.map(interest => (
                <button
                  key={interest}
                  type="button"
                  onClick={() => onInterestToggle(interest)}
                  data-testid={`interest-${interest.toLowerCase()}`}
                  className="px-3 py-2 rounded-full text-sm font-medium transition-all"
                  style={{
                    background: newRecipient.interests.includes(interest) 
                      ? '#9E6752' 
                      : (darkMode.isDark ? 'rgba(255, 255, 255, 0.1)' : '#F5F5F5'),
                    color: newRecipient.interests.includes(interest) 
                      ? 'white' 
                      : (darkMode.isDark ? '#FED7A5' : '#534145'),
                    border: `1px solid ${newRecipient.interests.includes(interest) ? '#9E6752' : (darkMode.isDark ? '#FED7A5' : '#E5E5E5')}`
                  }}
                >
                  {interest}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium" style={{ color: darkMode.isDark ? '#FED7A5' : '#20212B' }}>Notes (optional)</Label>
            <Textarea
              value={newRecipient.notes}
              onChange={(e) => onRecipientChange({ ...newRecipient, notes: e.target.value })}
              data-testid="recipient-notes-input"
              placeholder="Favorite colors, sizes, things they've mentioned wanting..."
              className="mt-2"
              style={{ 
                backgroundColor: darkMode.isDark ? 'rgba(255, 255, 255, 0.1)' : 'white',
                color: darkMode.isDark ? '#FED7A5' : '#1F2937',
                borderColor: darkMode.isDark ? '#FED7A5' : '#E5E5E5'
              }}
              rows={3}
            />
          </div>

          <div>
            <Label className="text-sm font-medium mb-3 block" style={{ color: darkMode.isDark ? '#FED7A5' : '#20212B' }}>
              Occasions (optional)
            </Label>
            
            {/* Selected Occasions with Dates */}
            {newRecipient.occasions.length > 0 && (
              <div className="space-y-3 mb-3">
                {newRecipient.occasions.map(occasion => (
                  <div
                    key={occasion.name}
                    className="p-3 rounded-lg"
                    style={{
                      background: darkMode.isDark ? 'rgba(158, 103, 82, 0.2)' : 'rgba(158, 103, 82, 0.1)',
                      border: `1px solid ${darkMode.isDark ? 'rgba(254, 215, 165, 0.3)' : '#E8D4B4'}`
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm" style={{ color: darkMode.isDark ? '#FED7A5' : '#9E6752' }}>
                        {occasion.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => onRemoveOccasion(occasion.name)}
                        className="p-1 rounded-full hover:scale-110 transition-transform"
                        style={{ background: darkMode.isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)' }}
                      >
                        <X className="w-3.5 h-3.5" style={{ color: darkMode.isDark ? '#FED7A5' : '#9E6752' }} />
                      </button>
                    </div>
                    <Input
                      type="date"
                      value={occasion.date}
                      onChange={(e) => onOccasionDateChange(occasion.name, e.target.value)}
                      placeholder="Select date"
                      style={{
                        backgroundColor: darkMode.isDark ? 'rgba(255, 255, 255, 0.1)' : 'white',
                        color: darkMode.isDark ? '#FED7A5' : '#1F2937',
                        borderColor: darkMode.isDark ? '#FED7A5' : '#D1D5DB',
                        colorScheme: darkMode.isDark ? 'dark' : 'light'
                      }}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Add Occasion Dropdown */}
            <Select onValueChange={onAddOccasion}>
              <SelectTrigger 
                className="mt-2" 
                style={{ 
                  backgroundColor: darkMode.isDark ? 'rgba(255, 255, 255, 0.1)' : 'white',
                  color: darkMode.isDark ? '#FED7A5' : '#1F2937',
                  borderColor: darkMode.isDark ? '#FED7A5' : '#E5E5E5'
                }}
              >
                <div className="flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  <span>Add Occasion</span>
                </div>
              </SelectTrigger>
              <SelectContent>
                {occasionOptions
                  .filter(occ => !newRecipient.occasions.find(o => o.name === occ))
                  .map(occasion => (
                    <SelectItem key={occasion} value={occasion}>
                      {occasion}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-xs mt-2" style={{ color: darkMode.isDark ? 'rgba(254, 215, 165, 0.7)' : '#73766A' }}>
              Add occasions with dates - we'll send you reminders before each one!
            </p>
          </div>

          <Button
            type="submit"
            data-testid="submit-recipient"
            className="w-full transition-all hover:scale-[1.02]"
            style={{ background: darkMode.isDark ? '#9E6752' : '#20212B', color: '#FED7A5', borderRadius: '30px', padding: '14px', fontSize: '16px' }}
          >
            Create Keepsake
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
